import {
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  importProvidersFrom,
  provideZonelessChangeDetection,
} from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { provideRouter, withInMemoryScrolling } from "@angular/router";

import {
  AdviceAidCommonModule,
  BugsnagFilter,
  getReleaseStage,
  setWireValidationReporter,
} from "@aa/common";
import { routes, CanMatchQna } from "./app.routes";
import { provideCore } from "./core";

import Bugsnag, { BrowserConfig } from "@bugsnag/js";
import BugsnagPerformance from "@bugsnag/browser-performance";
import { BugsnagErrorHandler } from "@bugsnag/plugin-angular";
import { environment } from "../environments/environment";

// configure Bugsnag (at module load, before bootstrap). The release stage comes
// from the server-stamped window.appConfig (one prod build serves staging and
// production). Reporting is limited to the deployed stages via
// enabledReleaseStages, so local dev and e2e runs (which report "development")
// initialise the SDK but send no events.
const bugsnagConfig: Pick<
  BrowserConfig,
  "apiKey" | "appVersion" | "releaseStage" | "enabledReleaseStages" | "onError"
> = {
  apiKey: "48b0a86a5783ee1c9e6ce926598e74e9",
  appVersion: environment.version,
  releaseStage: getReleaseStage(environment),
  enabledReleaseStages: ["staging", "production"],
  // Drop injected/extension-script noise (chiefly iOS on-device page
  // translation): a report whose stack is entirely foreign (frames present but
  // none from our bundle) can never symbolicate against our maps and is not our
  // code. Stackless reports are kept so genuine errors aren't lost. See #183.
  onError: (event) =>
    BugsnagFilter.shouldReport(
      (event.errors[0]?.stacktrace ?? []).map((frame) => frame.file),
    ),
};

Bugsnag.start(bugsnagConfig);
BugsnagPerformance.start(bugsnagConfig);

// Forward Zod wire-schema validation failures to Bugsnag. The common library
// has no Bugsnag dependency, so the reporter is registered here at bootstrap
// (mirrors projects/app). Without this, selfserv wire-validation failures
// (e.g. the server-injected window.selfServMeta) were console-only with no
// Bugsnag visibility. See design/wiki/wire-schemas.md.
setWireValidationReporter(({ schemaName, issues, data }) => {
  const error = new Error(`Wire schema validation failed: ${schemaName}`);
  error.name = "WireValidationError";
  Bugsnag.notify(error, (event) => {
    event.addMetadata("wire", { schemaName, issues, data });
  });
});

export function errorHandlerFactory() {
  return new BugsnagErrorHandler();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withInMemoryScrolling({ anchorScrolling: "enabled" }),
    ),
    CanMatchQna,
    provideCore(),
    importProvidersFrom(AdviceAidCommonModule, BrowserModule),
    { provide: LOCALE_ID, useValue: "en-GB" },
    { provide: ErrorHandler, useFactory: errorHandlerFactory },
  ],
};
